import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { UseFormRegister, FieldErrors, UseFormTrigger, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThemedDatePicker } from '../common/themed-datepicker.component';
import { ATTACHMENT_ACCEPT } from '../../utils/attachment.utils';

interface AllowanceOutsideSwedenFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  isTaxFree: string;
  selectedFiles: File[];
  tripStartDate: string;
  tripEndDate: string;
  travelDays: number;
  allowancePerDay: string;
  total: string;
  countries: Array<any>;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
  vatFromPreferences?: number;
  existingFileName?: string;
  onDownloadExistingFile?: () => void;
  isDownloadingFile?: boolean;
}

const AllowanceOutsideSwedenForm: React.FC<AllowanceOutsideSwedenFormProps> = ({
  register,
  errors,
  trigger,
  setValue,
  isTaxFree,
  selectedFiles,
  tripStartDate,
  tripEndDate,
  travelDays,
  allowancePerDay,
  total,
  countries,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData,
  vatFromPreferences,
  existingFileName,
  onDownloadExistingFile,
  isDownloadingFile
}) => {
  const { t } = useTranslation();

  // Dropdown states
  const [isTaxFreeDropdownOpen, setIsTaxFreeDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countryDropdownPosition, setCountryDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [isFoodAllowanceDropdownOpen, setIsFoodAllowanceDropdownOpen] = useState(false);

  // Refs for click outside
  const taxFreeDropdownRef = useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = useRef<HTMLDivElement>(null);
  const isPdfAttachedDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownPortalRef = useRef<HTMLDivElement>(null);
  const foodAllowanceDropdownRef = useRef<HTMLDivElement>(null);

  // Options
  const taxFreeOptions = [
    { value: '', label: t('SELECT_OPTION') },
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const foodAllowanceOptions = [
    { value: 'no_food_expense', label: t('NO_FOOD_EXPENSE'), name: t('NO_FOOD_EXPENSE'), amount: 0 },
    { value: 'breakfast', label: t('BREAKFAST'), name: t('BREAKFAST_NAME'), amount: 60 },
    { value: 'lunch_or_dinner', label: t('LUNCH_OR_DINNER'), name: t('LUNCH_OR_DINNER_NAME'), amount: 105 },
    { value: 'lunch_and_dinner', label: t('LUNCH_AND_DINNER'), name: t('LUNCH_AND_DINNER_NAME'), amount: 210 },
    { value: 'breakfast_lunch_and_dinner', label: t('BREAKFAST_LUNCH_AND_DINNER'), name: t('BREAKFAST_LUNCH_AND_DINNER_NAME'), amount: 270 },
  ];

  const normalizeFoodAllowance = (value: any): string => {
    const validKeys = ['no_food_expense', 'breakfast', 'lunch_or_dinner', 'lunch_and_dinner', 'breakfast_lunch_and_dinner'];
    if (typeof value === 'string' && validKeys.includes(value)) return value;
    const amountToKey: Record<number, string> = { 0: 'no_food_expense', 60: 'breakfast', 105: 'lunch_or_dinner', 210: 'lunch_and_dinner', 270: 'breakfast_lunch_and_dinner' };
    const num = parseFloat(value);
    return amountToKey[num] || 'no_food_expense';
  };
  const currentFoodAllowance = normalizeFoodAllowance(formData?.foodAllowance);

  const foodAllowanceAmount = foodAllowanceOptions.find(o => o.value === currentFoodAllowance)?.amount ?? 0;
  const netTotal = (parseFloat(total || '0') - (travelDays * foodAllowanceAmount)).toFixed(2);

  // Helper functions
  const getTaxFreeLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getYesNoLabel = (value: string) => {
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getCountryLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_COUNTRY');
    return value;
  };

  const getFoodAllowanceLabel = (value: string) => {
    const option = foodAllowanceOptions.find(o => o.value === value);
    return option ? option.label : t('SELECT_OPTION');
  };

  const getFoodAllowanceName = (value: string) => {
    const option = foodAllowanceOptions.find(o => o.value === value);
    return option ? option.name : '';
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taxFreeDropdownRef.current && !taxFreeDropdownRef.current.contains(event.target as Node)) {
        setIsTaxFreeDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
      // Check if click is outside country dropdown trigger AND portal
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node) &&
          countryDropdownPortalRef.current && !countryDropdownPortalRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
      if (foodAllowanceDropdownRef.current && !foodAllowanceDropdownRef.current.contains(event.target as Node)) {
        setIsFoodAllowanceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate country dropdown position
  const calculateCountryDropdownPosition = () => {
    if (countryDropdownRef.current) {
      const rect = countryDropdownRef.current.getBoundingClientRect();
      setCountryDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Update dropdown position on scroll
  useEffect(() => {
    if (countryDropdownOpen) {
      const handleScroll = () => {
        calculateCountryDropdownPosition();
      };

      // Add scroll listener to window and all parent elements
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [countryDropdownOpen]);

  const validateEndDate = (endDate: string) => {
    if (!tripStartDate || !endDate) return true;
    return new Date(endDate) >= new Date(tripStartDate) || t('END_DATE_AFTER_START');
  };

  return (
    <>
      {/* Row 1: Is Tax Free & Show on Invoice */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('IS_ALLOWANCE_OUTSIDE_TAX_FREE')}</label>
          <div ref={taxFreeDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isTaxFree', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsTaxFreeDropdownOpen(!isTaxFreeDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isTaxFree ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!isTaxFree || isTaxFree === '') ? '#68727d' : '#252525',
                boxShadow: isTaxFreeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isTaxFreeDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getTaxFreeLabel(isTaxFree)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isTaxFreeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isTaxFreeDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isTaxFree', option.value);
                      trigger('isTaxFree');
                      setIsTaxFreeDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: isTaxFree === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isTaxFree && (
            <div className="error-message">{errors.isTaxFree.message?.toString()}</div>
          )}
        </div>

        {/* Show on Invoice — always visible, regardless of the tax-free selection */}
        {(
          <div className="form-group">
            <label>{t('SHOW_ON_INVOICE_QUESTION')}</label>
            <div ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
              <input type="hidden" {...register('showOnInvoice')} />
              <div
                onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
                style={{
                  height: '46px',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                  outline: showOnInvoiceDropdownOpen ? '1px solid #7c3aed' : 'none'
                }}
              >
                <span>{getYesNoLabel(formData?.showOnInvoice || 'yes')}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {showOnInvoiceDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#fff',
                    border: '1px solid #eaebf0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    padding: '4px 0'
                  }}
                >
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onFormChange('showOnInvoice', option.value);
                        trigger('showOnInvoice');
                        setShowOnInvoiceDropdownOpen(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        margin: '0 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#252525',
                        backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f4efff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-allowance-outside-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-allowance-outside-pdf" 
              accept={ATTACHMENT_ACCEPT}
              onChange={onFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                    <a
                    href={URL.createObjectURL(file)}
                    download={file.name}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#7c3aed',
                    }}
                  >
                    {file.name}
                  </a>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {selectedFiles.length === 0 && existingFileName && (
            <div className="uploaded-files">
              <div className="file-tag">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); onDownloadExistingFile?.(); }}
                  style={{ cursor: isDownloadingFile ? 'wait' : 'pointer', textDecoration: 'underline', color: '#7c3aed' }}
                >
                  {existingFileName}
                </a>
                {isDownloadingFile && (
                  <span className="spinner-border spinner-border-sm" style={{ marginLeft: '8px' }} role="status" aria-hidden="true"></span>
                )}
              </div>
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show PDF to Customer - Always Visible */}
      {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPdfAttachedToInvoice')} />
            <div
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                height: '46px',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: '#252525',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPdfAttachedDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPdfAttachedDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {yesNoOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPdfAttachedToInvoice', option.value);
                      trigger('isPdfAttachedToInvoice');
                      setIsPdfAttachedDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Trip Started Date & Trip Ended Date */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('TRIP_STARTED_DATE')}</label>
          <ThemedDatePicker
            value={formData?.tripStartDate || tripStartDate || ''}
            onChange={(v) => {
              onFormChange('tripStartDate', v);
              setValue('tripStartDate', v, { shouldValidate: true });
              trigger('tripStartDate');
            }}
            hasError={!!errors.tripStartDate}
          />
          <input type="hidden" {...register('tripStartDate', { required: t('PLEASE_SELECT_START_DATE') })} />
          {errors.tripStartDate && (
            <div className="error-message">{errors.tripStartDate.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('TRIP_ENDED_DATE')}</label>
          <ThemedDatePicker
            value={formData?.tripEndDate || tripEndDate || ''}
            onChange={(v) => {
              onFormChange('tripEndDate', v);
              setValue('tripEndDate', v, { shouldValidate: true });
              trigger('tripEndDate');
            }}
            hasError={!!errors.tripEndDate}
          />
          <input type="hidden" {...register('tripEndDate', { required: t('PLEASE_SELECT_END_DATE'), validate: validateEndDate })} />
          {errors.tripEndDate && (
            <div className="error-message">{errors.tripEndDate.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 3: Reason For Trip & Country */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('REASON_FOR_TRIP')}</label>
          <input
            type="text"
            {...register('reason', { required: t('PLEASE_ENTER_REASON') })}
            className={`form-control ${errors.reason ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('reason', e.target.value);
              trigger('reason');
            }}
          />
          {errors.reason && (
            <div className="error-message">{errors.reason.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('COUNTRY')}</label>
          <div ref={countryDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('country', { required: t('PLEASE_SELECT_COUNTRY') })} />
            <div
              onClick={() => {
                if (!countryDropdownOpen) {
                  calculateCountryDropdownPosition();
                }
                setCountryDropdownOpen(!countryDropdownOpen);
              }}
              style={{
                height: '46px',
                border: errors.country ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!formData?.country || formData?.country === '') ? '#68727d' : '#252525',
                boxShadow: countryDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: countryDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getCountryLabel(formData?.country || '')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: countryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {countryDropdownOpen && countryDropdownPosition && ReactDOM.createPortal(
              <div
                ref={countryDropdownPortalRef}
                style={{
                  position: 'fixed',
                  top: `${countryDropdownPosition.top}px`,
                  left: `${countryDropdownPosition.left}px`,
                  width: `${countryDropdownPosition.width}px`,
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 9999,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '4px 0'
                }}
              >
                <div
                  onClick={() => {
                    onFormChange('country', '');
                    trigger('country');
                    setCountryDropdownOpen(false);
                  }}
                  style={{
                    padding: '12px 16px',
                    margin: '0 8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#252525',
                    backgroundColor: (!formData?.country || formData?.country === '') ? '#f4efff' : '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (formData?.country !== '') {
                      e.currentTarget.style.backgroundColor = '#f4efff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData?.country !== '') {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  {t('SELECT_COUNTRY')}
                </div>
                {countries.map((country, index) => (
                  <div
                    key={country.landellerOmråde || index}
                    onClick={() => {
                      onFormChange('country', country.landellerOmråde);
                      trigger('country');
                      setCountryDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: formData?.country === country.landellerOmråde ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (formData?.country !== country.landellerOmråde) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData?.country !== country.landellerOmråde) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {country.landellerOmråde}
                  </div>
                ))}
              </div>,
              document.body
            )}
          </div>
          {errors.country && (
            <div className="error-message">{errors.country.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 4: Destination (Full Width) */}
      <div className="form-group">
        <label>{t('DESTINATION')}</label>
        <input
          type="text"
          {...register('destination', { required: t('PLEASE_ENTER_DESTINATION') })}
          className={`form-control ${errors.destination ? 'error' : ''}`}
          placeholder={t('WRITE_HERE')}
          onChange={(e) => {
            onFormChange('destination', e.target.value);
            trigger('destination');
          }}
        />
        {errors.destination && (
          <div className="error-message">{errors.destination.message?.toString()}</div>
        )}
      </div>

      {/* Food Expense Dropdown - Full Width */}
      <div className="form-group">
        <label>{t('FOOD_EXPENSE')}</label>
        <div ref={foodAllowanceDropdownRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setIsFoodAllowanceDropdownOpen(!isFoodAllowanceDropdownOpen)}
            style={{
              height: '46px',
              border: '1px solid #eaebf0',
              borderRadius: '8px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              backgroundColor: '#fff',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '15px',
              fontWeight: 500,
              color: !currentFoodAllowance ? '#68727d' : '#252525',
              boxShadow: isFoodAllowanceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
              outline: isFoodAllowanceDropdownOpen ? '1px solid #7c3aed' : 'none'
            }}
          >
            <span>{getFoodAllowanceLabel(currentFoodAllowance)}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{
                transform: isFoodAllowanceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          {isFoodAllowanceDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: '#fff',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                overflow: 'hidden',
                padding: '4px 0'
              }}
            >
              {foodAllowanceOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    onFormChange('foodAllowance', option.value);
                    setIsFoodAllowanceDropdownOpen(false);
                  }}
                  style={{
                    padding: '12px 16px',
                    margin: '0 8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#252525',
                    backgroundColor: currentFoodAllowance === option.value ? '#f4efff' : '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (currentFoodAllowance !== option.value) {
                      e.currentTarget.style.backgroundColor = '#f4efff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentFoodAllowance !== option.value) {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary Section */}
      <div className="summary-section">
        <div className="summary-item">
          <span className="summary-label">
            {t('TRAVEL_DAYS')}: {travelDays}, {t('ALLOWANCE_PER_DAY')}: {allowancePerDay} {t('SEK')}
          </span>
          <span className="summary-value">{t('TOTAL')}: {netTotal} {t('SEK')}</span>
        </div>
        {foodAllowanceAmount > 0 && (
          <div className="summary-item" style={{ marginTop: '6px', fontSize: '13px' }}>
            <span>{t('FOOD_EXPENSE')}: {getFoodAllowanceName(currentFoodAllowance)}: -{travelDays * foodAllowanceAmount} {t('SEK')}</span>
          </div>
        )}
      </div>

      {/* VAT Info Display */}
      {vatFromPreferences !== undefined && (
        <div className="form-group">
          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '14px', color: '#666' }}>
            <strong>{t('VAT')}:</strong> {vatFromPreferences}% {t('FROM_INVOICE_PREFERENCES') || '(from invoice preferences)'}
          </div>
        </div>
      )}
    </>
  );
};

export default AllowanceOutsideSwedenForm;
